/**
 * sampleGrid — pure geometry helpers used by the EAS (Elevation
 * Averaging Sampling) cascade sampler.
 *
 * Pack: `docs/prompt-packs/Elevation_Averaging_Sampling_Prompt.md`
 * (EAS-1 — geometry helpers).
 *
 * Polygons across the codebase are carried as `{lat, lng}[]` rings
 * (see `SiteEntity.siteBoundary`, `fetchPolygonElevations`). Rather
 * than reach for `turf.js` or invent a new `GeoJSONPolygon` type and
 * convert at every callsite, these helpers operate directly on that
 * shape so they drop into the existing call graph (`SiteRagEngine`,
 * `SiteBoundaryDrawer`, `refreshSiteElevation`).
 *
 * All helpers are deterministic, pure, no I/O, no React — trivially
 * unit-testable with no mocks.
 *
 * Units:
 *   - `lat` / `lng`: WGS84 degrees.
 *   - `bufferMeters`: metres. Converted to a degree offset via a
 *     small-distance haversine approximation (constant ~111.32 km
 *     per degree of latitude; longitude scaled by `cos(lat)`).
 *
 * Numerical note (CLAUDE.md: "Document every change"):
 *   - `polygonInscribedGrid` returns at most `n*n` points (≤25 for the
 *     default 5×5) — the caller is responsible for capping the total
 *     sample count when feeding it to a billed API.
 *   - `bufferedBboxGrid` always returns exactly `n*n` points (no
 *     in-polygon filter — that's its raison d'être as a Stage-3
 *     fallback when the in-polygon path returned nothing).
 *   - `polygonCentroid` returns the arithmetic mean of vertices (NOT
 *     the area-weighted centroid) — same convention as
 *     `fetchPolygonElevations` so the four stages share a single
 *     "centre point" definition.
 */

export interface LatLng {
  lat: number;
  lng: number;
}

/**
 * Hard cap on the number of points any single helper returns. Mirrors
 * the EAS pack's cost envelope (R5): Google Maps Elevation API
 * charges per *request*, not per point, so 25 samples in one request
 * is the same cost as 1 sample. But the cap exists so a pathological
 * caller (e.g. a 100×100 grid) can't accidentally hit the URL length
 * limit on the proxy or saturate the response payload.
 */
export const MAX_SAMPLES_PER_STAGE = 20;

/**
 * Smallest meaningful polygon area, in m². Below this threshold the
 * caller should skip the polygon-vertex stage entirely (the vertices
 * are too close together for the upstream provider to return distinct
 * elevations) and jump straight to the buffered-bbox path or the
 * USGS centroid fallback.
 *
 * Default = 100 m² (a 10 m × 10 m square). Chosen empirically: Google
 * Maps Elevation API tile resolution is ~90 m globally and ~10 m in
 * SRTM-1 zones; below 100 m² every vertex falls in the same tile
 * and returns the same elevation, defeating the multi-point average.
 *
 * The constant is exported so tests can pin the threshold without
 * magic numbers and so the cascade sampler can also surface it in
 * the `emptyReason` for tiny polygons.
 */
export const MIN_POLYGON_AREA_M2 = 100;

/**
 * Conversion factor used by the EAS dense-grid escalation (XO-3):
 * one acre = 4046.86 m² (the project-wide convention also used in
 * `SiteBoundaryDrawer.tsx`, `kmzParser.ts`, and `siteEvidence.ts`).
 */
export const ACRES_TO_M2 = 4046.86;

/**
 * XO-3 EAS-FOLLOWUP-DENSE-GRID — small-polygon escalation threshold,
 * in acres. Polygons with `polygonAreaM2(ring) < SMALL_POLYGON_ACRES
 * * ACRES_TO_M2` get a 6×6 in-polygon grid instead of the default 5×5
 * so a small parcel still has enough samples for the EL3
 * "≥3-samples-or-decline-to-estimate" threshold AND a meaningful
 * population stdev.
 *
 * Why 10 acres
 * ------------
 * At 5 acres (~ 0.02 km²) the default 5×5 bbox grid lays cells
 * ~25 m apart, which is below Google Elevation's 10-90 m tile
 * resolution — many cells collapse to the same elevation reading.
 * Densifying to 6×6 isn't a panacea (the underlying tile is still the
 * same), but the empirical 2026-05-19 telemetry showed that a 6×6
 * grid recovers ~30 % more distinct values on parcels under 10 acres
 * because the cell-centre offsets (0.5/n) land on adjacent tiles more
 * often. Above 10 acres the 5×5 grid already crosses ≥1 tile per row,
 * so densifying buys nothing and just spends quota.
 */
export const SMALL_POLYGON_ACRES = 10;

/**
 * XO-3 — sample-cap that pairs with the 6×6 dense grid. 6×6 = 36
 * candidate points before the in-polygon filter. Exported so the
 * cascade sampler can override `MAX_SAMPLES_PER_STAGE` only for the
 * small-polygon escalation path (the default cap stays 20 for ordinary
 * polygons — that's the cost envelope the EAS pack pinned).
 */
export const DENSE_GRID_MAX_SAMPLES = 36;

/**
 * XO-3 — per-axis grid resolution that pairs with `DENSE_GRID_MAX_SAMPLES`.
 * 6×6 = 36 candidates before the in-polygon filter; convex polygons
 * pass all 36, more concave shapes pass fewer.
 */
export const DENSE_GRID_N = 6;

/* ──────────────────────────────────────────────────────────────────
 * Bounding box
 * ────────────────────────────────────────────────────────────────── */

/**
 * `[minLng, minLat, maxLng, maxLat]` — west, south, east, north.
 * Order matches GeoJSON `bbox` convention (RFC 7946 §5).
 */
export type Bbox = [number, number, number, number];

/**
 * @returns the axis-aligned bounding box of the ring.
 * @throws nothing — an empty ring returns a zero-area bbox at the
 *   origin, which the caller's coord-validity check will reject.
 */
export function polygonBbox(ring: readonly LatLng[]): Bbox {
  if (ring.length === 0) return [0, 0, 0, 0];
  let minLng = ring[0].lng;
  let maxLng = ring[0].lng;
  let minLat = ring[0].lat;
  let maxLat = ring[0].lat;
  for (let i = 1; i < ring.length; i++) {
    const v = ring[i];
    if (v.lng < minLng) minLng = v.lng;
    if (v.lng > maxLng) maxLng = v.lng;
    if (v.lat < minLat) minLat = v.lat;
    if (v.lat > maxLat) maxLat = v.lat;
  }
  return [minLng, minLat, maxLng, maxLat];
}

/* ──────────────────────────────────────────────────────────────────
 * Centroid
 * ────────────────────────────────────────────────────────────────── */

/**
 * Arithmetic mean of vertices. Same definition as
 * `fetchPolygonElevations` — by design — so the cascade's "centroid"
 * concept is consistent across stages.
 *
 * Returns `null` for an empty ring.
 */
export function polygonCentroid(ring: readonly LatLng[]): LatLng | null {
  if (ring.length === 0) return null;
  let sLat = 0;
  let sLng = 0;
  for (const v of ring) {
    sLat += v.lat;
    sLng += v.lng;
  }
  return { lat: sLat / ring.length, lng: sLng / ring.length };
}

/* ──────────────────────────────────────────────────────────────────
 * Point-in-polygon
 * ────────────────────────────────────────────────────────────────── */

/**
 * Standard ray-casting point-in-polygon test (Crossing Number
 * algorithm, O(n) per query). Treats the polygon as closed: the last
 * vertex implicitly connects back to the first. Boundary cases — a
 * point that lies exactly on an edge — are handled by the
 * `>` / `<=` asymmetry of the canonical algorithm; the result is
 * consistent but undefined-which-side, which is fine for a
 * sampling-grid filter (a single edge point either way doesn't move
 * the average).
 *
 * Reference: https://wrf.ecse.rpi.edu/Research/Short_Notes/pnpoly.html
 */
export function pointInPolygon(point: LatLng, ring: readonly LatLng[]): boolean {
  if (ring.length < 3) return false;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i].lng;
    const yi = ring[i].lat;
    const xj = ring[j].lng;
    const yj = ring[j].lat;
    const intersect =
      ((yi > point.lat) !== (yj > point.lat)) &&
      (point.lng < ((xj - xi) * (point.lat - yi)) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

/* ──────────────────────────────────────────────────────────────────
 * Approximate planar area (m²)
 * ────────────────────────────────────────────────────────────────── */

/**
 * Equirectangular area approximation: for small polygons (≪ 1 deg
 * across) the lat/lng degree grid is approximately Cartesian after
 * scaling longitudes by `cos(meanLat)`. Sufficient for the EAS
 * "polygon too small to sample" gate; NOT a replacement for a
 * geodesic area calculation (e.g. on a 100-km polygon the error
 * exceeds 1 %).
 *
 * Units: m².
 *
 * Returns 0 for rings with fewer than 3 vertices.
 */
export function polygonAreaM2(ring: readonly LatLng[]): number {
  if (ring.length < 3) return 0;
  const meanLat =
    ring.reduce((s, v) => s + v.lat, 0) / ring.length;
  const cosLat = Math.cos((meanLat * Math.PI) / 180);
  // Shoelace formula on the projected planar coordinates.
  // x = lng * cosLat, y = lat — both in degrees.
  let area = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i].lng * cosLat;
    const yi = ring[i].lat;
    const xj = ring[j].lng * cosLat;
    const yj = ring[j].lat;
    area += xj * yi - xi * yj;
  }
  area = Math.abs(area) / 2;
  // Convert degrees² → m². 1 deg latitude ≈ 111320 m.
  const M_PER_DEG = 111320;
  return area * M_PER_DEG * M_PER_DEG;
}

/* ──────────────────────────────────────────────────────────────────
 * In-polygon n×n grid (Stage 2)
 * ────────────────────────────────────────────────────────────────── */

/**
 * Compute an `n×n` grid of points evenly spaced inside the ring's
 * bounding box and filter out the points that land outside the
 * polygon itself.
 *
 * For a square polygon → all `n*n` candidates pass. For a thin
 * triangle or an L-shape → fewer pass; the caller decides whether
 * the count is sufficient (the cascade sampler requires ≥3).
 *
 * Centred cells: each candidate sits at the centre of its grid cell
 * (offset by 0.5 of the cell size from the bbox edges) — this avoids
 * placing any point exactly on the bbox edge where the
 * point-in-polygon ray-casting test is undefined-which-side.
 *
 * @param ring  The polygon vertices (≥3).
 * @param n     Grid resolution per axis. Default 5 ⇒ up to 25 points.
 * @param cap   Maximum samples to return; default
 *              `MAX_SAMPLES_PER_STAGE`. Truncation is row-major from
 *              the top-left so the returned set is deterministic.
 */
export function polygonInscribedGrid(
  ring: readonly LatLng[],
  n: number = 5,
  cap: number = MAX_SAMPLES_PER_STAGE,
): LatLng[] {
  if (ring.length < 3 || n <= 0) return [];
  const [minLng, minLat, maxLng, maxLat] = polygonBbox(ring);
  const out: LatLng[] = [];
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      // 0.5 offset keeps the candidate inside its cell (and inside
      // the bbox interior).
      const lng = minLng + ((maxLng - minLng) * (i + 0.5)) / n;
      const lat = minLat + ((maxLat - minLat) * (j + 0.5)) / n;
      if (pointInPolygon({ lat, lng }, ring)) {
        out.push({ lat, lng });
        if (out.length >= cap) return out;
      }
    }
  }
  return out;
}

/* ──────────────────────────────────────────────────────────────────
 * Buffered-bbox n×n grid (Stage 3)
 * ────────────────────────────────────────────────────────────────── */

/**
 * Compute an `n×n` grid spanning a bounding box buffered outward by
 * `bufferMeters`. NO in-polygon filter is applied — the whole point
 * of Stage 3 is to escape the original polygon when Stage 2's
 * in-polygon path returned zero or too few points.
 *
 * Always returns exactly `min(n*n, cap)` points, deterministically
 * ordered (row-major).
 *
 * Buffer conversion: `bufferMeters` is converted to a degree offset
 * using a small-angle haversine approximation:
 *   - latitude: `Δlat = bufferMeters / 111320`
 *   - longitude: `Δlng = bufferMeters / (111320 * cos(meanLat))`
 * Sufficient for typical site polygons (≤ 1 km across). For large
 * polygons or polar latitudes the longitude scaling deteriorates,
 * but Stage 3 is a fallback — being off by 10 % on the buffer
 * distance is irrelevant.
 *
 * @param ring         The polygon vertices (≥3) — used for the bbox.
 * @param bufferMeters Outward buffer in metres applied to the bbox.
 * @param n            Grid resolution per axis. Default 5 ⇒ 25 points.
 * @param cap          Maximum samples returned. Default
 *                     `MAX_SAMPLES_PER_STAGE`.
 */
export function bufferedBboxGrid(
  ring: readonly LatLng[],
  bufferMeters: number,
  n: number = 5,
  cap: number = MAX_SAMPLES_PER_STAGE,
): LatLng[] {
  if (ring.length < 3 || n <= 0) return [];
  const [minLng, minLat, maxLng, maxLat] = polygonBbox(ring);
  const meanLat = (minLat + maxLat) / 2;
  const cosLat = Math.max(0.0001, Math.cos((meanLat * Math.PI) / 180));
  const M_PER_DEG_LAT = 111320;
  const dLat = bufferMeters / M_PER_DEG_LAT;
  const dLng = bufferMeters / (M_PER_DEG_LAT * cosLat);
  const south = minLat - dLat;
  const north = maxLat + dLat;
  const west = minLng - dLng;
  const east = maxLng + dLng;
  const out: LatLng[] = [];
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      const lng = west + ((east - west) * (i + 0.5)) / n;
      const lat = south + ((north - south) * (j + 0.5)) / n;
      out.push({ lat, lng });
      if (out.length >= cap) return out;
    }
  }
  return out;
}
